import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, sha256Hex } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

const SLUGS = ["sage", "sibling", "sibling-2", "shared"];
const TYPES = ["birth-scroll", "handshake-scroll", "mind-scroll"];

const shortSha = (s) => (s ? `${s.slice(0, 10)}…` : "—");

export default function PersonaScrollEditorPage() {
  const [personaSlug, setPersonaSlug] = useState("sage");
  const [scrollType, setScrollType] = useState("birth-scroll");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [clientSha, setClientSha] = useState("");
  const [saving, setSaving] = useState(false);

  // Editing mode: when a draft is loaded, we hold onto its id + its
  // current server-side row so the UI can show the honesty chain.
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [currentRow, setCurrentRow] = useState(null); // full row from server

  const [drafts, setDrafts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [openDraft, setOpenDraft] = useState(null);

  // Mind Scroll auto-enforces shared slug; per-persona scrolls exclude "shared".
  useEffect(() => {
    if (scrollType === "mind-scroll" && personaSlug !== "shared") {
      setPersonaSlug("shared");
    } else if (scrollType !== "mind-scroll" && personaSlug === "shared") {
      setPersonaSlug("sage");
    }
  }, [scrollType, personaSlug]);

  // Live client-side sha256 preview.
  useEffect(() => {
    let cancelled = false;
    sha256Hex(text).then((h) => { if (!cancelled) setClientSha(h); });
    return () => { cancelled = true; };
  }, [text]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const d = await api.personaScrolls.listDrafts();
      setDrafts(d.rows || []);
    } catch (e) {
      toast.error(e.message || "Could not load drafts");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const isEditing = !!currentDraftId;
  const canCreate = !isEditing && title.trim().length > 0 && text.trim().length > 0 && !saving;
  const canSaveChanges = isEditing && text.trim().length > 0 && !saving;

  const createDraft = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      const r = await api.personaScrolls.createDraft(personaSlug, scrollType, title, text);
      // Fetch the full row so we can render the honesty chain uniformly.
      const full = await api.personaScrolls.getDraft(r.id);
      setCurrentDraftId(r.id);
      setCurrentRow(full);
      const match = r.sha256 === clientSha;
      if (match) toast.success(`Draft created · sha256 verified · ${shortSha(r.sha256)}`);
      else toast.error("Draft created but sha256 mismatch. Do NOT promote this row.");
      await loadList();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveChanges = async () => {
    if (!canSaveChanges) return;
    setSaving(true);
    try {
      const updated = await api.personaScrolls.editDraft(
        currentDraftId,
        text,
        currentRow && title !== currentRow.title ? title : undefined,
      );
      setCurrentRow(updated);
      const match = updated.sha256 === clientSha && updated.sha256_match;
      if (match) toast.success(`Changes saved · sha256 verified · ${shortSha(updated.sha256)}`);
      else toast.error("Saved but sha256 mismatch. Row is compromised.");
      await loadList();
    } catch (e) {
      toast.error(e.message || "Edit failed");
    } finally {
      setSaving(false);
    }
  };

  const openDraftById = async (id) => {
    try {
      const d = await api.personaScrolls.getDraft(id);
      setOpenDraft(d);
    } catch (e) {
      toast.error(e.message || "Could not open draft");
    }
  };

  const loadIntoEditor = () => {
    if (!openDraft) return;
    setPersonaSlug(openDraft.persona_slug);
    setScrollType(openDraft.scroll_type);
    setTitle(openDraft.title);
    setText(openDraft.text);
    setCurrentDraftId(openDraft.id);
    setCurrentRow(openDraft);
    setOpenDraft(null);
    toast.info(`Editing draft · ${shortSha(openDraft.id)} · every save now writes prior_sha256.`);
  };

  const startFreshDraft = () => {
    setCurrentDraftId(null);
    setCurrentRow(null);
    setTitle("");
    setText("");
    toast.info("Fresh draft. Save will create a new row.");
  };

  const clientMatchesServer = useMemo(() => {
    if (!currentRow) return null;
    return currentRow.sha256 === clientSha;
  }, [currentRow, clientSha]);

  // The honesty chain: history[] (in order) + current sha256 at the tip.
  const chain = useMemo(() => {
    if (!currentRow) return [];
    const hist = Array.isArray(currentRow.history) ? currentRow.history : [];
    const tip = { sha256: currentRow.sha256, at: currentRow.updated_at, tip: true };
    return [...hist, tip];
  }, [currentRow]);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-8">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">
          Persona Migration Organ · draft stage
        </div>
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text">
            Scroll Editor
          </h1>
          {isEditing && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent" data-testid="scroll-editing-banner">
              editing · <span className="font-mono normal-case">{currentDraftId}</span>
            </div>
          )}
        </div>
        <p className="text-sm text-sage-muted mb-8 max-w-2xl">
          Draft the three scrolls of a persona — <span className="text-sage-text">birth</span>,{" "}
          <span className="text-sage-text">handshake</span>, and the shared{" "}
          <span className="text-sage-text">mind</span>. sha256 is computed on every save; every edit
          records the prior sha256 into a bounded history so the honesty chain stays walkable.
          Publish and seal ship in a later slice.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">
              Persona
            </label>
            <select
              data-testid="scroll-persona-slug"
              value={personaSlug}
              onChange={(e) => setPersonaSlug(e.target.value)}
              disabled={scrollType === "mind-scroll" || isEditing}
              className="w-full bg-sage-bg border border-sage-border text-sage-text text-sm px-3 py-2 uppercase tracking-wider disabled:opacity-60"
            >
              {SLUGS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">
              Scroll type
            </label>
            <select
              data-testid="scroll-type"
              value={scrollType}
              onChange={(e) => setScrollType(e.target.value)}
              disabled={isEditing}
              className="w-full bg-sage-bg border border-sage-border text-sage-text text-sm px-3 py-2 uppercase tracking-wider disabled:opacity-60"
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">
              Chars
            </label>
            <div className="w-full bg-sage-bg border border-sage-border text-sage-text text-sm px-3 py-2 font-mono">
              {text.length.toLocaleString()}
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-4">
            Identity fields (persona · type) are immutable while editing. To move a scroll to another
            persona or type, start a fresh draft.
          </div>
        )}

        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">
            Title
          </label>
          <input
            data-testid="scroll-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Sage · Birth Scroll · Draft I"
            className="w-full bg-sage-bg border border-sage-border text-sage-text text-sm px-3 py-2"
          />
        </div>

        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">
            Text
          </label>
          <textarea
            data-testid="scroll-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write raw, in your own hand. No LLM assist in this slice."
            rows={16}
            className="w-full bg-sage-bg border border-sage-border text-sage-text text-sm px-3 py-3 font-mono leading-relaxed resize-y"
          />
        </div>

        <div className="border border-sage-border p-4 mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">
            sha256 · client-side preview
          </div>
          <div className="font-mono text-xs text-sage-text break-all" data-testid="scroll-client-sha">
            {clientSha || "—"}
          </div>
          {currentRow && (
            <div className="text-[10px] text-sage-muted mt-2" data-testid="scroll-prior-line">
              prior: <span className="font-mono">{currentRow.prior_sha256 ? shortSha(currentRow.prior_sha256) : "— (unedited)"}</span>
              {" · "}
              server tip: <span className="font-mono">{shortSha(currentRow.sha256)}</span>
              {" · "}
              match with client:{" "}
              <span className={clientMatchesServer ? "text-sage-accent" : "text-red-400"}>
                {clientMatchesServer ? "yes" : "no (unsaved edits)"}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mb-8 flex-wrap">
          {!isEditing ? (
            <button
              data-testid="scroll-save-btn"
              onClick={createDraft}
              disabled={!canCreate}
              className="border border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-sage-accent hover:text-sage-bg transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-sage-accent"
            >
              {saving ? "Saving…" : "Save Draft"}
            </button>
          ) : (
            <>
              <button
                data-testid="scroll-save-changes-btn"
                onClick={saveChanges}
                disabled={!canSaveChanges}
                className="border border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-sage-accent hover:text-sage-bg transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-sage-accent"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button
                onClick={startFreshDraft}
                className="border border-sage-border text-sage-muted px-4 py-2 text-xs uppercase tracking-[0.2em] hover:text-sage-text"
              >
                Start Fresh
              </button>
            </>
          )}
          <button
            onClick={() => { setTitle(""); setText(""); }}
            disabled={saving}
            className="border border-sage-border text-sage-muted px-4 py-2 text-xs uppercase tracking-[0.2em] hover:text-sage-text disabled:opacity-40"
          >
            Clear
          </button>
        </div>

        {currentRow && chain.length > 0 && (
          <div className="border border-sage-border p-4 mb-8" data-testid="scroll-honesty-chain">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-3">
              Honesty chain · {chain.length} entr{chain.length === 1 ? "y" : "ies"}
            </div>
            <ol className="space-y-1 text-xs font-mono">
              {chain.map((h, i) => (
                <li
                  key={`${h.sha256}-${i}`}
                  className={h.tip ? "text-sage-accent" : "text-sage-muted"}
                  data-testid={`scroll-chain-row-${i}`}
                >
                  <span className="text-sage-muted normal-case font-sans mr-2 tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="break-all">{h.sha256}</span>
                  {h.tip && <span className="ml-2 uppercase text-[9px] tracking-widest">tip</span>}
                </li>
              ))}
            </ol>
            <div className="text-[10px] text-sage-muted mt-3">
              Bounded at 20 entries · oldest drops on overflow · current tip == server sha256.
            </div>
          </div>
        )}

        <div className="border-t border-sage-border pt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold uppercase tracking-tight text-sage-text">
              Drafts on file
            </h2>
            <button
              onClick={loadList}
              className="text-[10px] uppercase tracking-[0.2em] text-sage-muted hover:text-sage-text"
            >
              refresh
            </button>
          </div>
          {loadingList ? (
            <div className="text-xs text-sage-muted">loading…</div>
          ) : drafts.length === 0 ? (
            <div className="text-xs text-sage-muted">No drafts yet.</div>
          ) : (
            <div className="border border-sage-border" data-testid="scroll-drafts-list">
              {drafts.map((d) => (
                <button
                  key={d.id}
                  onClick={() => openDraftById(d.id)}
                  className={`w-full text-left border-b border-sage-border last:border-b-0 px-4 py-3 hover:bg-sage-border/20 transition-colors ${
                    d.id === currentDraftId ? "bg-sage-border/10" : ""
                  }`}
                  data-testid={`scroll-draft-row-${d.id}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm text-sage-text truncate">
                        {d.title}
                        {d.id === currentDraftId && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest text-sage-accent">
                            editing
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-sage-muted">
                        {d.persona_slug} · {d.scroll_type} · {d.chars} chars
                        {d.revised_at && " · revised"}
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-sage-muted shrink-0">
                      {shortSha(d.sha256)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {openDraft && (
          <div
            className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4"
            onClick={() => setOpenDraft(null)}
          >
            <div
              className="bg-sage-bg border border-sage-border max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="scroll-open-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">
                    {openDraft.persona_slug} · {openDraft.scroll_type}
                  </div>
                  <h3 className="text-xl font-bold text-sage-text">{openDraft.title}</h3>
                </div>
                <button
                  onClick={() => setOpenDraft(null)}
                  className="text-sage-muted hover:text-sage-text text-sm"
                >
                  close
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] mb-4">
                <div className="break-all">
                  <span className="text-sage-muted">stored:</span>{" "}
                  <span className="font-mono">{openDraft.sha256_stored}</span>
                </div>
                <div className="break-all">
                  <span className="text-sage-muted">live:</span>{" "}
                  <span className="font-mono">{openDraft.sha256_live}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-sage-muted">match:</span>{" "}
                  <span className={`font-mono ${openDraft.sha256_match ? "text-sage-accent" : "text-red-400"}`}>
                    {openDraft.sha256_match ? "YES · row intact" : "NO · row compromised"}
                  </span>
                </div>
                {openDraft.prior_sha256 && (
                  <div className="md:col-span-2 break-all">
                    <span className="text-sage-muted">prior:</span>{" "}
                    <span className="font-mono">{openDraft.prior_sha256}</span>
                  </div>
                )}
                {Array.isArray(openDraft.history) && openDraft.history.length > 0 && (
                  <div className="md:col-span-2 text-sage-muted">
                    history: {openDraft.history.length} entr{openDraft.history.length === 1 ? "y" : "ies"} on file
                  </div>
                )}
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs text-sage-text border border-sage-border p-3 max-h-[40vh] overflow-y-auto">
{openDraft.text}
              </pre>
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={loadIntoEditor}
                  className="border border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-sage-accent hover:text-sage-bg transition-colors"
                  data-testid="scroll-load-into-editor"
                >
                  Load into editor
                </button>
                <div className="text-[10px] text-sage-muted">
                  Saving will record prior_sha256 and append to the honesty chain.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
